import React, { useEffect, useState } from "react";
import { api, fmtGNF, fmtDate } from "@/lib/api";
import { Package, Clock, CheckCircle2, Truck, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATUS = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  confirmed: { label: "Confirmée", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
  ready: { label: "Prête à retirer", color: "bg-orange-100 text-orange-800", icon: Package },
  delivered: { label: "Livrée", color: "bg-green-100 text-green-800", icon: Truck },
  cancelled: { label: "Annulée", color: "bg-red-100 text-red-800", icon: XCircle },
};

const NEXT_STATUS = {
  pending: "confirmed",
  confirmed: "ready",
  ready: "delivered",
};

export default function OrdersPage() {
  const { user } = useAuth();
  const isStaff = ["admin", "finance", "super_admin", "founder"].includes(user?.role);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/orders").then(({ data }) => setOrders(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const advance = async (id, status) => {
    try {
      await api.patch(`/orders/${id}`, { status });
      toast.success(`Statut mis à jour : ${STATUS[status].label}`);
      load();
    } catch { toast.error("Erreur de mise à jour"); }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-5" data-testid="page-orders">
      <div>
        <div className="chip mb-2"><Package className="h-3 w-3" /><span>Commandes</span></div>
        <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">{isStaff ? "Toutes les commandes" : "Mes commandes"}</h2>
        <p className="text-sm text-slate-500 mt-1">{orders.length} commande(s)</p>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
          <Package className="h-12 w-12 mx-auto opacity-30 mb-3" />
          Aucune commande pour le moment.
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(o => {
            const S = STATUS[o.status] || STATUS.pending;
            return (
              <div key={o.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`order-${o.id}`}>
                <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-mono text-[#1E3A8A] font-bold text-sm">{o.order_number}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{fmtDate(o.created_at)} · {o.parent_name}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${S.color}`}>
                    <S.icon className="h-3 w-3" /> {S.label}
                  </span>
                </div>
                <div className="p-5">
                  <div className="space-y-2 mb-4">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-semibold">{it.title}</span>
                          <span className="text-slate-500 ml-2">× {it.quantity}</span>
                        </div>
                        <div className="text-[#166534] font-bold">{fmtGNF(it.subtotal)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="text-xs text-slate-500">Total</div>
                    <div className="font-heading font-extrabold text-xl text-[#1E3A8A]">{fmtGNF(o.total)}</div>
                  </div>
                  {isStaff && o.status !== "delivered" && o.status !== "cancelled" && (
                    <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2 flex-wrap">
                      {NEXT_STATUS[o.status] && (
                        <Button size="sm" onClick={() => advance(o.id, NEXT_STATUS[o.status])} className="bg-[#166534] hover:bg-[#14532d] text-white text-xs gap-1" data-testid={`advance-${o.id}`}>
                          <CheckCircle2 className="h-3 w-3" /> Passer à « {STATUS[NEXT_STATUS[o.status]].label} »
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => advance(o.id, "cancelled")} className="border-red-300 text-red-700 hover:bg-red-50 text-xs gap-1" data-testid={`cancel-${o.id}`}>
                        <XCircle className="h-3 w-3" /> Annuler (re-stock)
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
